import { createApi } from "@reduxjs/toolkit/query/react";
import {
  GetPostsParams,
  getPostsQuery,
  ILikeResponse,
  IPost,
  IPostsResponse,
  IZapResponse,
} from "./postApiData";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { draftsApi } from "./draftsApi";
import { updatePost } from "../slice/updatedPostsSlice";
import { setEditingPostLoading } from "../slice/loadingSlice";

export const postApi = createApi({
  reducerPath: "postApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Post"],
  endpoints: (build) => ({
    getPosts: build.query<IPostsResponse, GetPostsParams>({
      query: getPostsQuery,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ uid }) => ({ type: "Post" as const, id: uid })),
              { type: "Post", id: "LIST" },
            ]
          : [{ type: "Post", id: "LIST" }],
      keepUnusedDataFor: 0,
    }),
    getPostByUid: build.query<IPost, string>({
      query: (uid) => `/posts/${uid}`,
      keepUnusedDataFor: 0,
      providesTags: (_res, _err, uid) => [{ type: "Post", id: uid }],
    }),
    createPost: build.mutation<IPost, Partial<IPost>>({
      query: (newPost) => ({
        url: "/posts",
        method: "POST",
        body: newPost,
      }),
      invalidatesTags: [{ type: "Post", id: "LIST" }],
    }),
    ////
    createLongPost: build.mutation<any, FormData>({
      query: (formData) => ({
        url: "/posts",
        method: "POST",
        body: formData,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(draftsApi.util.invalidateTags([{ type: "Draft", id: "COUNT" }]));
        } catch {}
      },
      invalidatesTags: [{ type: "Post", id: "LIST" }],
    }),

    updatePost: build.mutation<IPost, { formData: FormData; uid: string }>({
      query: ({ formData, uid }) => ({
        url: `/posts/${uid}`,
        method: "PATCH",
        body: formData,
      }),

      async onQueryStarted({ uid }, { dispatch, queryFulfilled }) {
        dispatch(setEditingPostLoading({ loading: true, postId: uid }));

        try {
          const updatedPost = await queryFulfilled;

          dispatch(updatePost(updatedPost.data));
        } catch (error) {
        } finally {
          dispatch(setEditingPostLoading({ loading: false, postId: null }));
        }
      },
      invalidatesTags: (_res, _err, { uid }) => [{ type: "Post", id: uid }],
    }),

    uploadImageLongPost: build.mutation<any, FormData>({
      query: (formData) => ({
        url: "/inline-images/upload",
        method: "POST",
        body: formData,
      }),
    }),

    deletePost: build.mutation<void, string>({
      query: (uid) => ({
        url: `/posts/${uid}`,
        method: "DELETE",
      }),
    }),
    likePost: build.mutation({
      query: (uid) => ({
        url: `/posts/${uid}/like`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Post", id: "LIST" }],
    }),
    unLike: build.mutation({
      query: (uid) => ({
        url: `/posts/${uid}/like`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Post", id: "LIST" }],
    }),

    //!sentiment
    sentimentPost: build.mutation({
      query: (uid) => ({
        url: `/posts/${uid}/sentiment`,
        method: "POST",
      }),
    }),
    updateSentiment: build.mutation({
      query: (uid) => ({
        url: `/posts/${uid}/sentiment`,
        method: "PATCH",
      }),
    }),
    savePost: build.mutation({
      query: (uid) => ({
        url: `/saved-posts/${uid}`,
        method: "POST",
      }),
    }),
    savePostDelete: build.mutation({
      query: (uid) => ({
        url: `/saved-posts/${uid}`,
        method: "DELETE",
      }),
    }),
    getSavedPosts: build.query<IPost[], { type?: "FREE" | "PAID" | "PREMIUM" }>({
      query: ({ type }) => ({
        url: "/saved-posts",
        params: { type },
      }),
      transformResponse: (response: { data: { savedAt: string; post: IPost }[] }) => {
        return response.data.map((item) => ({
          ...item.post,
        }));
      },
      providesTags: (result) =>
        result ? result.map(({ uid }) => ({ type: "Post" as const, id: uid })) : [],
    }),

    getPostLikes: build.query<ILikeResponse, { uid: string; page?: number; limit?: number }>({
      query: ({ uid, page = 1, limit = 20 }) => ({
        url: `/posts/${uid}/reactions/likes`,
        params: { page, limit },
      }),
    }),
    getPostZaps: build.query<
      IZapResponse,
      {
        uid: string;
        page?: number;
        limit?: number;
        sortBy?: "recent" | "amount";
        order?: "asc" | "desc";
      }
    >({
      query: ({ uid, page = 1, limit = 20, sortBy = "recent", order = "desc" }) => ({
        url: `/posts/${uid}/reactions/zaps`,
        params: { page, limit, sortBy, order },
      }),
    }),
    deleteAttachment: build.mutation<void, { uid: string; attachmentUid: string }>({
      query: ({ uid, attachmentUid }) => ({
        url: `/posts/${uid}/attachments/${attachmentUid}`,
        method: "DELETE",
      }),
    }),
    getFeedNewCount: build.query<{ count: number }, void>({
      query: () => "/posts/feed/new-count",
    }),
  }),
});

export const {
  useGetPostsQuery,
  useLazyGetPostsQuery,
  useGetPostByUidQuery,
  useCreatePostMutation,
  useCreateLongPostMutation,
  useUploadImageLongPostMutation,
  useUpdatePostMutation,
  useDeletePostMutation,
  useLikePostMutation,
  useUnLikeMutation,
  useSentimentPostMutation,
  useUpdateSentimentMutation,
  useSavePostMutation,
  useSavePostDeleteMutation,
  useGetSavedPostsQuery,
  useGetPostLikesQuery,
  useGetPostZapsQuery,
  useDeleteAttachmentMutation,
  useLazyGetFeedNewCountQuery,
} = postApi;
